if (!customElements.get("paginate-button")) {
  customElements.define(
    "paginate-button",
    class PaginateButton extends HTMLElement {
      constructor() {
        super();

        this.init();
      }

      init() {
        this.collectionPaginate = this.closest("collection-paginate");
        this.addEventListener("click", this.buttonClick.bind(this));
        this.addEventListener("keyup", this.buttonKeyboard.bind(this));
      }

      buttonClick() {
        this.requestNextPage();
      }
      buttonKeyboard(e) {
        if (e.keyCode == 32 || e.keyCode == 13) {
          e.preventDefault();
          this.requestNextPage();
        }
      }

      async requestNextPage() {
        if (this.isLoading) return;

        this.isLoading = true;
        this.showLoadingState();
        try {
          await this.collectionPaginate.extendPage();
        } catch (error) {
          console.error("Unable to load the next collection page", error);
        } finally {
          this.isLoading = false;
          if (this.isConnected) {
            this.restoreLoadingState();
          }
        }
      }

      showLoadingState() {
        this.defaultMarkup = this.defaultMarkup || this.innerHTML;
        this.innerHTML = "Loading...";
      }

      restoreLoadingState() {
        if (this.defaultMarkup) {
          this.innerHTML = this.defaultMarkup;
        }
      }
    }
  );
}

if (!customElements.get("collection-paginate")) {
  customElements.define(
    "collection-paginate",
    class CollectionPaginate extends HTMLElement {
      constructor() {
        super();

        this.init();
      }

      init() {
        // 1) Prefer state from filter-tabs (JS source of truth)
        const filterTabs = document.querySelector('filter-tabs');
        let tagsFromTabs = [];

        if (filterTabs) {
          const activeButtons = filterTabs.querySelectorAll('filter-button.filter-active');
          tagsFromTabs = Array.from(activeButtons)
            .map(btn => btn.dataset.value)
            .filter(Boolean);
        }

        // 2) Fallback: JSON from #activeFiltersJson (works on plain collection, but usually empty on /search)
        let tagsFromJson = [];
        const jsonScript = this.querySelector('#activeFiltersJson');
        if (jsonScript) {
          try {
            const data = JSON.parse(jsonScript.textContent || jsonScript.innerHTML || '{}');
            tagsFromJson = (data.tags || []).map(tagData => tagData.tag).filter(Boolean);
          } catch (e) {
            console.error('Failed to parse #activeFiltersJson', e);
          }
        }

        // 3) Only set activetags if we actually found something
        if (!this.activetags || !Array.isArray(this.activetags) || this.activetags.length === 0) {
          this.activetags = tagsFromTabs.length > 0 ? tagsFromTabs : tagsFromJson;
        }

        if (!Array.isArray(this.activetags)) {
          this.activetags = [];
        }

        this.collectionUrl = this.dataset.collUrl;
        this.collectionHandle = this.getAttribute("collection-handle"); // Store handle

        // necessary html components
        this.activeFiltersContainers = document.querySelectorAll(".active--collection-filters");
        this.prodGridContainer = this.querySelector("#ProductGridContainer");

        // components for plp pagination
        this.plpContainer = this.querySelector(".collection");
        this.currentPage = parseInt(this.plpContainer.dataset.currentPage);
        this.totalPages = parseInt(this.plpContainer.dataset.totalPages);
        this.prodGrid = this.plpContainer.querySelector("#product-grid");
        this.paginateIndicator = this.plpContainer.querySelector(".paginate-indicator");
      }

      connectedCallback() {
        this.renderActiveFilters(this.activetags);
        this.updateViewMoreCount();

        const hasPendingSizeFilter =
          this.getSizeTags().length > 1 &&
          this.plpContainer?.dataset.totalProducts === "0";
        if (hasPendingSizeFilter) {
          this.fetchFilterProdList();
        }
      }

      showLoadingMotion() {
        return `
        <div class="loading-motion">
          <p style="padding: 2rem; text-align: center; color: #666;">Loading...</p>
        </div>
      `;
      }

      showFilterError() {
        return `
          <div class="filter-results-error" role="alert" style="padding: 2rem; text-align: center;">
            <p>Unable to load products. Please try again.</p>
            <button type="button" class="button" data-filter-retry>Try again</button>
          </div>
        `;
      }

      getSizeTags(tags = this.activetags) {
        return (Array.isArray(tags) ? tags : []).filter((tag) => /^size_/i.test(tag));
      }

      buildCollectionUrl(tags, page = 1, view = "") {
        const selected = Array.isArray(tags) ? tags.filter(Boolean) : [];
        const tagPath = selected.length
          ? `/${selected.map((tag) => encodeURIComponent(tag)).join("+")}`
          : "";
        const searchParams = new URLSearchParams(window.location.search);

        Array.from(searchParams.keys()).forEach((key) => {
          if (key.startsWith("filter.") || key === "q") {
            searchParams.delete(key);
          }
        });
        searchParams.delete("page");
        if (page > 1) {
          searchParams.set("page", page);
        }
        if (view) {
          searchParams.set("view", view);
        } else {
          searchParams.delete("view");
        }

        const query = searchParams.toString();
        return `${this.collectionUrl}${tagPath}${query ? `?${query}` : ""}`;
      }

      getNativeSizeFilterState(tags = this.activetags) {
        const selected = Array.isArray(tags) ? tags.filter(Boolean) : [];
        const sizeTags = this.getSizeTags(selected);
        const sizeValues = [];

        for (const tag of sizeTags) {
          const match = tag.match(/^size_(\d+(?:-\d+)?w?)$/i);
          if (!match) return null;

          sizeValues.push(match[1].replace("-", ".").toUpperCase());
        }

        return {
          sharedTags: selected.filter((tag) => !/^size_/i.test(tag)),
          sizeTags,
          sizeValues: [...new Set(sizeValues)],
        };
      }

      buildNativeSizeFilterUrl(tags, page = 1, view = "ajax") {
        const nativeFilter = this.getNativeSizeFilterState(tags);
        if (!nativeFilter || nativeFilter.sizeValues.length === 0) return null;

        const tagPath = nativeFilter.sharedTags.length
          ? `/${nativeFilter.sharedTags
            .map((tag) => encodeURIComponent(tag))
            .join("+")}`
          : "";
        const searchParams = new URLSearchParams(window.location.search);
        const sizeParam = "filter.v.option.size";

        searchParams.delete("page");
        searchParams.delete("view");
        searchParams.delete(sizeParam);

        nativeFilter.sizeValues.forEach((value) => {
          searchParams.append(sizeParam, value);
        });

        if (page > 1) {
          searchParams.set("page", page);
        }
        if (view) {
          searchParams.set("view", view);
        }

        const query = searchParams.toString();
        return `${this.collectionUrl}${tagPath}${query ? `?${query}` : ""}`;
      }

      async fetchCollectionUrl(url, signal) {
        this.filterDocumentCache = this.filterDocumentCache || new Map();

        if (this.filterDocumentCache.has(url)) {
          const cachedHtml = this.filterDocumentCache.get(url);
          this.filterDocumentCache.delete(url);
          this.filterDocumentCache.set(url, cachedHtml);
          const cachedWrapper = document.createElement("div");
          cachedWrapper.innerHTML = cachedHtml;
          return cachedWrapper;
        }

        const response = await fetch(url, {
          signal,
        });
        if (!response.ok) {
          throw new Error(`Unable to load filtered collection (${response.status})`);
        }

        const html = await response.text();
        this.filterDocumentCache.set(url, html);
        const maxCacheEntries = 20;
        if (this.filterDocumentCache.size > maxCacheEntries) {
          const oldestUrl = this.filterDocumentCache.keys().next().value;
          this.filterDocumentCache.delete(oldestUrl);
        }
        const wrapper = document.createElement("div");
        wrapper.innerHTML = html;
        return wrapper;
      }

      async fetchCollectionDocument(tags, page = 1, signal) {
        return this.fetchCollectionUrl(
          this.buildCollectionUrl(tags, page, "ajax"),
          signal
        );
      }

      async fetchNativeSizeFilterDocument(tags, page = 1, signal) {
        const url = this.buildNativeSizeFilterUrl(tags, page, "ajax");
        if (!url) return null;

        return this.fetchCollectionUrl(url, signal);
      }

      hasFilteredCollectionMarkup(wrapper) {
        // Active filter metadata is not reliable in Shopify alternate views.
        // A successful native response with collection markup is authoritative.
        return Boolean(
          wrapper?.querySelector(
            "collection-paginate #ProductGridContainer .collection"
          )
        );
      }

      setProductCountLoading(isLoading) {
        const counter = document.querySelector("[data-product-count]");
        if (!counter) return;

        if (isLoading) {
          counter.dataset.filterPending = "true";
          counter.textContent = "Loading products…";
        } else {
          delete counter.dataset.filterPending;
        }
      }

      updateProductCount() {
        const counter = document.querySelector("[data-product-count]");
        const total = parseInt(this.plpContainer?.dataset.totalProducts, 10);
        if (!counter || !Number.isFinite(total)) return;

        counter.textContent = total === 1 ? "1 product" : `${total} products`;
      }

      async mapWithConcurrency(items, concurrency, mapper) {
        const results = new Array(items.length);
        let nextIndex = 0;
        let firstError = null;

        const worker = async () => {
          while (!firstError) {
            const index = nextIndex;
            nextIndex += 1;
            if (index >= items.length) return;

            try {
              results[index] = await mapper(items[index], index);
            } catch (error) {
              firstError = error;
            }
          }
        };

        const workerCount = Math.min(concurrency, items.length);
        await Promise.all(
          Array.from({ length: workerCount }, () => worker())
        );
        if (firstError) throw firstError;
        return results;
      }

      async fetchInitialSizeUnionDocuments(selected, signal) {
        const sizeTags = this.getSizeTags(selected);
        const sharedTags = selected.filter((tag) => !/^size_/i.test(tag));
        const tagGroups = sizeTags.map((sizeTag) => [...sharedTags, sizeTag]);
        const firstPages = await this.mapWithConcurrency(
          tagGroups,
          4,
          (tags) => this.fetchCollectionDocument(tags, 1, signal)
        );
        const remainingPages = [];

        firstPages.forEach((wrapper, index) => {
          const collectionState = wrapper.querySelector(
            "collection-paginate .collection"
          );
          const totalPages = parseInt(collectionState?.dataset.totalPages, 10) || 1;

          for (let page = 2; page <= totalPages; page += 1) {
            remainingPages.push({ tags: tagGroups[index], page });
          }
        });

        return { firstPages, remainingPages };
      }

      async fetchRemainingSizeUnionDocuments(pageRequests, signal) {
        return this.mapWithConcurrency(
          pageRequests,
          4,
          ({ tags, page }) => this.fetchCollectionDocument(tags, page, signal)
        );
      }

      mergeFilteredGrids(wrappers) {
        const seenProducts = new Set();
        const productMarkup = [];
        const firstGrid = wrappers[0]?.querySelector("#product-grid");
        const promoTiles = firstGrid
          ? Array.from(firstGrid.children)
            .map((item, index) => (
              item.matches(".plp-promo-tile")
                ? { index, markup: item.outerHTML }
                : null
            ))
            .filter(Boolean)
          : [];

        wrappers.forEach((wrapper) => {
          wrapper
            .querySelectorAll("#product-grid > .grid__item:not(.plp-promo-tile)")
            .forEach((item) => {
              const productId = item.querySelector(
                "product-block[data-product-id]"
              )?.dataset.productId;
              const productUrl = item
                .querySelector('a[href*="/products/"]')
                ?.getAttribute("href")
                ?.split("?")[0];
              const key = productId || productUrl;

              if (key && !seenProducts.has(key)) {
                seenProducts.add(key);
                productMarkup.push(item.outerHTML);
              }
            });
        });

        const mergedMarkup = [...productMarkup];
        promoTiles.forEach((promo) => {
          mergedMarkup.splice(
            Math.min(promo.index, mergedMarkup.length),
            0,
            promo.markup
          );
        });

        return {
          markup: mergedMarkup.join(""),
          productCount: productMarkup.length,
        };
      }

      replaceCollectionMarkup(wrapper, activeTags) {
        const replacement = wrapper.querySelector("collection-paginate");
        if (!replacement) {
          throw new Error("Filtered collection markup was not found");
        }

        const savedHandle = this.getAttribute("collection-handle");
        const savedUrl = this.dataset.collUrl;

        this.innerHTML = replacement.innerHTML;
        if (savedHandle) this.setAttribute("collection-handle", savedHandle);
        if (savedUrl) this.dataset.collUrl = savedUrl;

        this.activetags = [...activeTags];
        this.init();
      }

      renderActiveFilters(tags) {
        const collectionHandle = this.getAttribute("collection-handle");

        const newHTML =
          tags.length > 0
            ? `
            <p>Filter By</p>
            <div class="active--collection-filters__container">
              <ul class="active-filters__list">
                ${collectionHandle === "baby" ||
              collectionHandle === "shop-all" ||
              collectionHandle === "kids"
              ? tags
                .map((tag) => {
                  const tagData = tag.split("_");
                  return `<li><remove-filter role="button" tabindex="0" class="active-filter" data-filter-value=${tag}>${tagData[0] === "size" ? tagData[1] : (tagData[1] || "").replace("&", " & ").replace("$", " $")
                    } x</remove-filter></li>`;
                })
                .join("")
              : tags
                .map((tag) => {
                  const tagData = tag.split("_");
                  return `<li><remove-filter role="button" tabindex="0" class="active-filter" data-filter-value=${tag}>${tagData[0] === "size"
                    ? tagData[1].replace("-", ".")
                    : tagData[1].replace("&", " & ").replace("$", " $")
                    } x</remove-filter></li>`;
                })
                .join("")
            }
              </ul>
              <remove-filter role="button" tabindex="0" id="resetFilters" data-filter-value="clear">
                Clear Filters
              </remove-filter>
            </div>
          `
            : "";

        this.activeFiltersContainers.forEach(container => {
          container.innerHTML = newHTML;
        });
      }

      filterProductsByCollection(wrapper) {
        if (!this.collectionHandle || this.collectionHandle === 'all') return;

        const products = wrapper.querySelectorAll('#product-grid .grid__item');
        products.forEach(product => {
          const handles = product.dataset.collectionHandles;
          if (handles && !handles.split(',').includes(this.collectionHandle)) {
            product.remove();
          }
        });
      }

      updateViewMoreCount() {
        if (!this.prodGrid || !this.paginateIndicator) return;

        const btn = this.paginateIndicator.querySelector('.paginate-more');
        if (!btn) return;

        const totalCount = parseInt(btn.dataset.totalCount, 10);
        if (!Number.isFinite(totalCount)) return;

        const loadedCount = this.prodGrid.querySelectorAll('.grid__item:not(.plp-promo-tile)').length;
        const countSpan = btn.querySelector('.view-more-count');

        if (countSpan) {
          countSpan.textContent = `${loadedCount}/${totalCount}`;
        }
      }

      async fetchFilterProdList() {
        const selected = Array.isArray(this.activetags)
          ? [...this.activetags]
          : [];
        const hasFilters = Array.isArray(selected) && selected.length > 0;
        const sizeTags = this.getSizeTags(selected);
        const hasSizeFilters = sizeTags.length > 0;
        const hasMultipleSizes = sizeTags.length > 1;
        const canUseNativeSizeFilter = Boolean(
          hasMultipleSizes && this.getNativeSizeFilterState(selected)
        );
        const requestVersion = (this.filterRequestVersion || 0) + 1;
        this.filterRequestVersion = requestVersion;
        if (this.filterAbortController) {
          this.filterAbortController.abort();
        }
        this.filterAbortController = new AbortController();
        const { signal } = this.filterAbortController;
        const url = this.buildCollectionUrl(selected);
        const productCounter = document.querySelector("[data-product-count]");
        const previousProductCountText = productCounter?.textContent || "";
        const productCountWasPending = Boolean(
          productCounter?.dataset.filterPending
        );

        history.replaceState({}, "current_filter", url);
        this.nativeSizeFilterMode = false;
        this.setProductCountLoading(hasSizeFilters);
        this.prodGridContainer.innerHTML = this.showLoadingMotion();

        try {
          if (canUseNativeSizeFilter) {
            const nativeWrapper = await this.fetchNativeSizeFilterDocument(
              selected,
              1,
              signal
            );
            if (requestVersion !== this.filterRequestVersion) return;

            if (this.hasFilteredCollectionMarkup(nativeWrapper)) {
              const newPaginationRow = nativeWrapper.querySelector(
                '.pagination-row[data-ajax-container]'
              );
              const currentPaginationRow = document.querySelector(
                '.pagination-row[data-ajax-container]'
              );

              this.replaceCollectionMarkup(nativeWrapper, selected);
              this.nativeSizeFilterMode = true;

              if (newPaginationRow && currentPaginationRow) {
                currentPaginationRow.innerHTML = newPaginationRow.innerHTML;
              }

              this.renderActiveFilters(selected);
              this.setProductCountLoading(false);
              this.updateProductCount();
              this.updateViewMoreCount();
              return;
            }
          }

          if (hasMultipleSizes) {
            const { firstPages, remainingPages } =
              await this.fetchInitialSizeUnionDocuments(selected, signal);
            if (requestVersion !== this.filterRequestVersion) return;

            const initialGrid = this.mergeFilteredGrids(firstPages);
            this.replaceCollectionMarkup(firstPages[0], selected);

            this.prodGrid.innerHTML = initialGrid.markup;
            this.plpContainer.dataset.currentPage = "1";
            this.plpContainer.dataset.totalPages = "1";
            this.plpContainer.dataset.totalProducts = String(
              initialGrid.productCount
            );
            this.currentPage = 1;
            this.totalPages = 1;
            this.paginateIndicator.innerHTML = remainingPages.length
              ? '<div class="filter-results-loading" role="status">Loading more products...</div>'
              : "";

            const currentPaginationRow = document.querySelector('.pagination-row[data-ajax-container]');
            if (currentPaginationRow) {
              currentPaginationRow.innerHTML = "";
            }

            this.renderActiveFilters(selected);
            this.updateViewMoreCount();

            if (remainingPages.length) {
              const remainingWrappers =
                await this.fetchRemainingSizeUnionDocuments(remainingPages, signal);
              if (requestVersion !== this.filterRequestVersion) return;

              const completeGrid = this.mergeFilteredGrids([
                ...firstPages,
                ...remainingWrappers,
              ]);
              this.prodGrid.innerHTML = completeGrid.markup;
              this.plpContainer.dataset.totalProducts = String(
                completeGrid.productCount
              );
              this.paginateIndicator.innerHTML = "";
              this.renderActiveFilters(selected);
              this.updateViewMoreCount();
            }

            this.setProductCountLoading(false);
            this.updateProductCount();
            return;
          }

          const wrapper = await this.fetchCollectionDocument(selected, 1, signal);
          if (requestVersion !== this.filterRequestVersion) return;

          const newPaginationRow = wrapper.querySelector('.pagination-row[data-ajax-container]');
          const currentPaginationRow = document.querySelector('.pagination-row[data-ajax-container]');

          this.replaceCollectionMarkup(wrapper, selected);

          if (newPaginationRow && currentPaginationRow) {
            currentPaginationRow.innerHTML = newPaginationRow.innerHTML;
          }

          this.renderActiveFilters(hasFilters ? selected : []);
          this.setProductCountLoading(false);
          this.updateProductCount();
          this.updateViewMoreCount();
        } catch (err) {
          if (err.name === "AbortError") return;
          if (requestVersion !== this.filterRequestVersion) return;

          this.prodGridContainer.innerHTML = this.showFilterError();
          this.prodGridContainer
            .querySelector("[data-filter-retry]")
            ?.addEventListener(
              "click",
              () => this.fetchFilterProdList(),
              { once: true }
            );
          this.setProductCountLoading(false);
          if (productCounter) {
            productCounter.textContent = productCountWasPending
              ? "Products unavailable"
              : previousProductCountText;
          }
          console.error(err);
        }
      }

      async extendPage() {
        if (this.currentPage >= this.totalPages) return;

        const hasFilters = Array.isArray(this.activetags) && this.activetags.length > 0;

        if (this.nativeSizeFilterMode) {
          const nextPage = this.currentPage + 1;
          const loadMoreBtn = this.paginateIndicator?.querySelector(".paginate-more");
          if (loadMoreBtn) {
            loadMoreBtn.showLoadingState?.();
          }
          const requestVersion = this.filterRequestVersion;

          try {
            const wrapper = await this.fetchNativeSizeFilterDocument(
              this.activetags,
              nextPage,
              this.filterAbortController?.signal
            );
            if (requestVersion !== this.filterRequestVersion) {
              loadMoreBtn?.restoreLoadingState?.();
              return;
            }
            const newCollection = wrapper?.querySelector(
              "#ProductGridContainer .collection"
            );
            const newGrid = newCollection?.querySelector("#product-grid");
            const newIndicator = newCollection?.querySelector(
              ".paginate-indicator"
            );

            if (!newCollection || !newGrid) {
              throw new Error("Native filtered pagination markup was not found");
            }

            this.prodGrid.insertAdjacentHTML("beforeend", newGrid.innerHTML);
            this.paginateIndicator.innerHTML = newIndicator?.innerHTML || "";
            this.currentPage =
              parseInt(newCollection.dataset.currentPage, 10) || nextPage;
            this.totalPages =
              parseInt(newCollection.dataset.totalPages, 10) || this.totalPages;
            this.plpContainer.dataset.currentPage = String(this.currentPage);
            this.plpContainer.dataset.totalPages = String(this.totalPages);
            this.renderActiveFilters(this.activetags || []);
            this.updateViewMoreCount();
          } catch (err) {
            loadMoreBtn?.restoreLoadingState?.();
            if (err.name === "AbortError") return;
            console.error("extendPage (native size filter) error", err);
          }

          return;
        }

        if (!hasFilters) {
          // ✅ ORIGINAL BEHAVIOR: no filters → load more from the collection
          const url = `${this.collectionUrl}?page=${this.currentPage + 1}`;
          window.history.pushState({}, '', url);

          try {
            const listToAppend = await fetch(url).then((response) => response.text());
            const listContainer = document.createElement("div");
            listContainer.innerHTML = listToAppend;

            const newCollPaginate = listContainer.querySelector("collection-paginate");

            this.currentPage = newCollPaginate.currentPage;
            if (newCollPaginate.prodGrid)
              this.prodGrid.innerHTML += newCollPaginate.prodGrid.innerHTML;
            if (newCollPaginate.paginateIndicator)
              this.paginateIndicator.innerHTML = newCollPaginate.paginateIndicator.innerHTML;

            this.updateViewMoreCount();
          } catch (err) {
            console.error(err);
          }

          return;
        }

        // A single active size tag still uses Shopify's normal collection pagination.
        const nextPage = this.currentPage + 1;

        const loadMoreBtn = this.paginateIndicator?.querySelector('.paginate-more');
        if (loadMoreBtn) {
          loadMoreBtn.innerHTML = 'Loading...';
        }

        const pageUrl = new URL(window.location.href);
        pageUrl.searchParams.set('page', nextPage);
        const tmpUrl = pageUrl.pathname + pageUrl.search;

        try {
          const html = await fetch(tmpUrl).then(r => r.text());
          const wrapper = document.createElement('div');
          wrapper.innerHTML = html;

          // Filter out products not in current collection
          this.filterProductsByCollection(wrapper);

          const newCollection = wrapper.querySelector('#ProductGridContainer .collection');
          if (!newCollection) {
            console.warn('extendPage: no .collection found in ajax response');
            return;
          }

          const newGrid = newCollection.querySelector('#product-grid');
          const newIndicator = newCollection.querySelector('.paginate-indicator');

          if (newGrid && this.prodGrid) {
            this.prodGrid.innerHTML += newGrid.innerHTML;
          }

          if (newIndicator && this.paginateIndicator) {
            this.paginateIndicator.innerHTML = newIndicator.innerHTML;
          }

          this.currentPage = parseInt(newCollection.dataset.currentPage);
          this.totalPages = parseInt(newCollection.dataset.totalPages);

          // keep active chips visible
          this.renderActiveFilters(this.activetags || []);
          this.updateViewMoreCount();
        } catch (err) {
          console.error('extendPage (search) error', err);
        }
      }
    }
  );
}

if (!customElements.get("remove-filter")) {
  customElements.define(
    "remove-filter",
    class RemoveFilter extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.filterValue = this.dataset.filterValue;

        this.parentCollection = document.querySelector("collection-paginate");
        this.addEventListener("click", this.onRemoveFilter.bind(this));
        this.addEventListener("keyup", this.onKeyUpRemoveFilter.bind(this));

        this.filterTabs = document.querySelector("filter-tabs");
      }

      onKeyUpRemoveFilter(e) {
        if (e.keyCode == 32 || e.keyCode == 13) {
          this.onRemoveFilter();
        }
      }

      onRemoveFilter() {
        let updatedFilters = [];
        switch (this.filterValue) {
          case "clear":
            this.parentCollection.activetags = [];
            this.filterTabs
              .querySelectorAll("filter-button.filter-active")
              .forEach((tab) => {
                tab.classList.remove("filter-active");
                tab.setAttribute("aria-checked", "false");
              });
            break;
          default:
            this.parentCollection.activetags = this.parentCollection.activetags.filter(
              (tag) => tag !== this.filterValue
            );
            if (this.filterTabs.querySelector(`filter-button[data-value="${this.filterValue}"]`)) {
              this.filterTabs
                .querySelector(`filter-button[data-value="${this.filterValue}"]`)
                .classList.remove("filter-active");
              this.filterTabs
                .querySelector(`filter-button[data-value="${this.filterValue}"]`)
                .setAttribute("aria-checked", "false");
            }
            break;
        }

        this.parentCollection.fetchFilterProdList();

        this.filterTabs.foldActiveContainer();
      }
    }
  );
}
